import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';

const SECTIONS = [
  { key: 'А', label: 'Стандартное оборудование' },
  { key: 'Б', label: 'Конструкции стенда' },
  { key: 'В', label: 'Художественное оформление' },
  { key: 'Г', label: 'Подвесные конструкции' },
  { key: 'Д', label: 'Работы и услуги' },
  { key: 'Е', label: 'Аудио-видео оборудование' },
  { key: 'Ж', label: 'Аккредитация площадки' },
  { key: 'З', label: 'Флористика и декор' },
  { key: 'И', label: 'Инсталляции / спецэффекты' },
];

const VENUES = ['Крокус Экспо', 'Экспоцентр', 'Тимирязев Центр', 'Другая'];
const STATUSES = { draft: 'Черновик', sent: 'Отправлено', won: 'Выиграно', lost: 'Проиграно' };
const STATUS_COLORS = { draft: '#888', sent: 'var(--accent)', won: 'var(--success)', lost: 'var(--danger)' };
const VAT_TYPES = { usn_5: 'УСН 5%', osno_20: 'ОСНО 20%', osno_22: 'ОСНО 22%' };
const VAT_RATES = { usn_5: 0.05, osno_20: 0.20, osno_22: 0.22 };
const UNITS = ['кв.м', 'п.м', 'шт', 'компл', 'день'];
const TERMINAL_MODELS = [
  ['gpt-5.6-luna', 'GPT-5.6 Luna ($)'],
  ['gpt-5.6-terra', 'GPT-5.6 Terra ($$)'],
  ['gpt-5.6-sol', 'GPT-5.6 Sol ($$$)'],
  ['gpt-6-astra', 'GPT-6 Astra ($$$$)'],
];

// Итог сметы. Цены в смете заведены уже С НДС (prices_vat_rate); для клиента с другой
// ставкой (УСН 5%, ОСНО 20%) вычитается разница НДС. Формула зеркалит server/estimate-tax.js
const DEFAULT_TAX = { prices_include_vat: true, prices_vat_rate: 22 };
function calcWithVat(afterDiscount, vatType, tax) {
  const clientRate = VAT_RATES[vatType] ?? 0.05;
  const base = tax.prices_include_vat ? afterDiscount / (1 + tax.prices_vat_rate / 100) : afterDiscount;
  return base * (1 + clientRate);
}
function taxFormula(vatType, tax) {
  const clientRate = VAT_RATES[vatType] ?? 0.05;
  const k = n => String(Math.round(n * 1000) / 1000).replace('.', ',');
  if (tax.prices_include_vat) {
    return `Цены в смете уже с НДС ${tax.prices_vat_rate}%: итого = сумма ÷ ${k(1 + tax.prices_vat_rate / 100)} × ${k(1 + clientRate)}`;
  }
  return `Цены без НДС: итого = сумма × ${k(1 + clientRate)}`;
}

function formatMoney(n) {
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('ru-RU'); } catch { return d; }
}

// ====== List View ======
function EstimatesList({ onOpen, onNew, onTraining, onTerminal, uiShow = () => true }) {
  const [estimates, setEstimates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [knStats, setKnStats] = useState(null);

  useEffect(() => {
    api.getEstimates().then(setEstimates).catch(() => {}).finally(() => setLoading(false));
    api.getKnowledgeStats().then(setKnStats).catch(() => {});
  }, []);

  const filtered = filter === 'all' ? estimates : estimates.filter(e => e.status === filter);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Загрузка...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Расчёт сметы</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
          {uiShow('ui_estimate_knowledge_stats') && knStats && (
            <button className="btn-sm" onClick={onTraining} title="База знаний AI — нажмите для обучения" style={{
              display: 'flex', gap: 10, alignItems: 'center', padding: '6px 14px',
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
            }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text2)', fontWeight: 600 }}>AI:</span>
              {[
                ['проектов', knStats.projects_total || knStats.projects_count || 0, knStats.projects_diff, 'var(--accent)'],
                ['позиций', knStats.total_items || 0, knStats.items_diff, 'var(--success)'],
                ['паттернов', knStats.total_patterns || 0, knStats.patterns_diff, 'var(--warning)'],
                ['файлов', knStats.total_files || 0, knStats.files_diff, 'var(--text2)'],
              ].map(([label, val, diff, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 700, color }}>{val}</span>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text2)' }}>{label}</span>
                  {diff > 0 && <span style={{ fontSize: '0.6rem', color: 'var(--success)', fontWeight: 700 }}>+{diff}</span>}
                </div>
              ))}
              <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 50, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: (knStats.intelligence_pct || 0) > 0 ? 'var(--accent)' : 'var(--border)', borderRadius: 3, width: `${Math.min(knStats.intelligence_pct || 0, 100)}%`, transition: 'width 0.5s' }} />
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: (knStats.intelligence_pct || 0) > 0 ? 'var(--accent)' : 'var(--text2)' }}>
                  {(knStats.intelligence_pct || 0) > 100 ? `+${knStats.intelligence_pct - 100}%` : `${knStats.intelligence_pct || 0}%`}
                </span>
              </div>
            </button>
          )}
          {onTerminal && <button className="btn-add" onClick={onTerminal} title="Диалог с ассистентом: корректировки для всей сметной системы"
            style={{ background: '#1d1d26', color: '#7CFC9A', border: '1px solid #7CFC9A', fontFamily: 'monospace' }}>&gt;_ ТЕРМИНАЛ</button>}
          {uiShow('ui_estimate_training') && <button className="btn-add" onClick={onTraining} style={{ background: 'var(--warning)', color: '#000' }}>ОБУЧИТЬ ПО ПРОЕКТАМ</button>}
          <button className="btn-add" onClick={onNew}>+ Новая смета</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className={`btn-sm ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}
          style={filter === 'all' ? { background: 'var(--accent)', color: '#fff', border: 'none' } : {}}>Все ({estimates.length})</button>
        {Object.entries(STATUSES).map(([k, v]) => {
          const cnt = estimates.filter(e => e.status === k).length;
          return (
            <button key={k} className={`btn-sm ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}
              style={filter === k ? { background: STATUS_COLORS[k], color: '#fff', border: 'none' } : {}}>
              {v} ({cnt})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
          {estimates.length === 0 ? 'Нет смет. Создайте первую!' : 'Нет смет с таким статусом'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {filtered.map(e => (
            <div key={e.id} onClick={() => onOpen(e.id)} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: 16, cursor: 'pointer', transition: 'border-color 0.15s',
            }}
              onMouseOver={ev => ev.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseOut={ev => ev.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{e.client_name || 'Без имени'}</div>
                <span style={{
                  fontSize: '0.75rem', padding: '2px 10px', borderRadius: 12,
                  background: STATUS_COLORS[e.status] + '22', color: STATUS_COLORS[e.status], fontWeight: 600,
                }}>{STATUSES[e.status]}</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: 8 }}>{e.project_title || '—'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span>{e.area_sqm ? e.area_sqm + ' кв.м' : ''} {e.venue ? '· ' + e.venue : ''}</span>
                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatMoney(e.total || 0)} ₽</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text2)', marginTop: 6 }}>
                {formatDate(e.updated_at)} · {e.creator_name || ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== Editor View ======
function EstimateEditor({ estimateId, onBack, isNew, uiShow = () => true }) {
  const [estimate, setEstimate] = useState(null);
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState('items');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState('');
  const [catalogSection, setCatalogSection] = useState('all');
  const [collapsed, setCollapsed] = useState({});
  const [commentText, setCommentText] = useState('');
  const [editingHeader, setEditingHeader] = useState(isNew);
  const [recognizing, setRecognizing] = useState(false);
  const [recognizeResult, setRecognizeResult] = useState(null);
  const [taxSettings, setTaxSettings] = useState(DEFAULT_TAX);
  const [standParams, setStandParams] = useState({ length: '', width: '', wall_height: '', has_suspension: false });
  const [aiModels, setAiModels] = useState({
    openai: true, openai_model: 'gpt-5.6-terra',
    claude: false, claude_model: 'claude-sonnet-4-20250514',
  });

  const load = useCallback(async () => {
    try {
      const data = await api.getEstimate(estimateId);
      setEstimate(data);
      setItems(data.items || []);
      setFiles(data.files || []);
      setComments(data.comments || []);
    } catch (err) {
      setMsg('Ошибка загрузки: ' + err.message);
    }
  }, [estimateId]);

  useEffect(() => {
    load();
    api.getEstimateTaxSettings().then(setTaxSettings).catch(() => {});
    api.getEstimateCatalog().then(c => {
      if (c.length === 0) {
        api.seedEstimateCatalog().then(() => api.getEstimateCatalog().then(setCatalog));
      } else {
        setCatalog(c);
      }
    }).catch(() => {});
  }, [load]);

  async function saveHeader(updates) {
    setSaving(true);
    try {
      await api.updateEstimate(estimateId, updates);
      setEstimate(prev => ({ ...prev, ...updates }));
      setMsg('Сохранено');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setSaving(false);
  }

  async function saveItems() {
    setSaving(true);
    try {
      await api.saveEstimateItems(estimateId, items);
      setMsg('Позиции сохранены');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setSaving(false);
  }

  function addItem(section) {
    setItems(prev => [...prev, { section, name: '', qty: 1, unit: 'шт', price: 0, note: '' }]);
  }

  function addFromCatalog(catItem, tier) {
    const priceKey = tier === 'economy' ? 'price_economy' : tier === 'premium' ? 'price_premium' : 'price_standard';
    setItems(prev => [...prev, {
      section: catItem.section, name: catItem.name, qty: 1, unit: catItem.unit,
      price: catItem[priceKey], note: catItem.note || '',
    }]);
    setShowCatalog(false);
  }

  function updateItem(idx, field, value) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  }

  function removeItem(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function moveItem(idx, dir) {
    setItems(prev => {
      const arr = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  }

  async function handleFileUpload(e) {
    const fileList = e.target.files;
    if (!fileList.length) return;
    try {
      const result = await api.uploadEstimateFiles(estimateId, Array.from(fileList));
      setFiles(prev => [...result, ...prev]);
      setMsg('Файлы загружены');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    e.target.value = '';
  }

  async function deleteFile(fileId) {
    if (!window.confirm('Удалить файл?')) return;
    try {
      await api.deleteEstimateFile(fileId);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  async function addComment() {
    if (!commentText.trim()) return;
    try {
      await api.addEstimateComment(estimateId, commentText);
      setCommentText('');
      load();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  async function deleteComment(commentId) {
    try {
      await api.deleteEstimateComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  async function handleRecognize() {
    const recognizableFiles = files.filter(f => /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(f.original_name || f.filename));
    if (recognizableFiles.length === 0) { setMsg('Нет изображений или PDF для анализа'); return; }
    setRecognizing(true);
    setRecognizeResult(null);
    try {
      const result = await api.recognizeEstimate(estimateId, aiModels, standParams);
      setRecognizeResult(result);
      setMsg('Анализ завершён');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setRecognizing(false);
  }

  function acceptRecognition() {
    if (!recognizeResult?.items) return;
    setItems(recognizeResult.items.map(it => ({
      section: it.section || 'А', name: it.name || '', qty: it.qty || 1,
      unit: it.unit || 'шт', price: it.price || 0, note: it.note || '',
    })));
    if (recognizeResult.description) {
      saveHeader({ notes: recognizeResult.description });
    }
    if (recognizeResult.area_sqm && !estimate.area_sqm) {
      saveHeader({ area_sqm: recognizeResult.area_sqm });
    }
    setTab('items');
    setMsg('Позиции загружены из AI-анализа. Не забудьте сохранить!');
  }

  async function handleDelete() {
    if (!window.confirm('Удалить эту смету полностью?')) return;
    try {
      await api.deleteEstimate(estimateId);
      onBack();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  if (!estimate) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Загрузка...</div>;

  const sectionTotals = {};
  let grandTotal = 0;
  for (const it of items) {
    const t = (it.qty || 0) * (it.price || 0);
    sectionTotals[it.section] = (sectionTotals[it.section] || 0) + t;
    grandTotal += t;
  }

  const discountStd = estimate.discount_standard || 0;
  const discountCli = estimate.discount_client || 0;
  const discountCust = estimate.discount_custom || 0;
  const totalDiscount = Math.min(discountStd + discountCli + discountCust, 100);
  const afterDiscount = grandTotal * (1 - totalDiscount / 100);
  const withVAT = calcWithVat(afterDiscount, estimate.vat_type, taxSettings);

  const filteredCatalog = catalog.filter(c => {
    if (catalogSection !== 'all' && c.section !== catalogSection) return false;
    if (catalogFilter && !c.name.toLowerCase().includes(catalogFilter.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn-sm" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Назад
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {msg && <span style={{ fontSize: '0.8rem', color: msg.startsWith('Ош') ? 'var(--danger)' : 'var(--success)' }}>{msg}</span>}
          <select value={estimate.status} onChange={e => saveHeader({ status: e.target.value })}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.85rem' }}>
            {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="btn-sm" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }} onClick={handleDelete}>Удалить</button>
        </div>
      </div>

      {/* Header */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editingHeader ? 12 : 0 }}>
          {!editingHeader ? (
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 4px' }}>{estimate.client_name || 'Клиент не указан'} — {estimate.project_title || 'Без названия'}</h3>
              <div style={{ fontSize: '0.85rem', color: 'var(--text2)' }}>
                {estimate.area_sqm ? estimate.area_sqm + ' кв.м' : ''} · {estimate.venue || '—'} · {estimate.exhibition_date ? formatDate(estimate.exhibition_date) : '—'} · {VAT_TYPES[estimate.vat_type]}
              </div>
            </div>
          ) : null}
          <button className="btn-sm" onClick={() => setEditingHeader(!editingHeader)}>
            {editingHeader ? 'Свернуть' : 'Редактировать'}
          </button>
        </div>

        {editingHeader && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Клиент</label>
              <input style={inp} value={estimate.client_name || ''} onChange={e => setEstimate(prev => ({ ...prev, client_name: e.target.value }))}
                onBlur={e => saveHeader({ client_name: e.target.value })} placeholder="Название компании" />
            </div>
            <div>
              <label style={lbl}>Название проекта</label>
              <input style={inp} value={estimate.project_title || ''} onChange={e => setEstimate(prev => ({ ...prev, project_title: e.target.value }))}
                onBlur={e => saveHeader({ project_title: e.target.value })} placeholder="Выставка / стенд" />
            </div>
            <div>
              <label style={lbl}>Площадь, кв.м</label>
              <input style={inp} type="number" value={estimate.area_sqm || ''} onChange={e => setEstimate(prev => ({ ...prev, area_sqm: parseFloat(e.target.value) || 0 }))}
                onBlur={e => saveHeader({ area_sqm: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label style={lbl}>Площадка</label>
              <select style={inp} value={estimate.venue || ''} onChange={e => { setEstimate(prev => ({ ...prev, venue: e.target.value })); saveHeader({ venue: e.target.value }); }}>
                <option value="">— Выберите —</option>
                {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Дата выставки</label>
              <input style={inp} type="date" value={estimate.exhibition_date || ''} onChange={e => { setEstimate(prev => ({ ...prev, exhibition_date: e.target.value })); saveHeader({ exhibition_date: e.target.value }); }} />
            </div>
            <div>
              <label style={lbl}>Тип НДС</label>
              <select style={inp} value={estimate.vat_type || 'usn_5'} onChange={e => { setEstimate(prev => ({ ...prev, vat_type: e.target.value })); saveHeader({ vat_type: e.target.value }); }}>
                {Object.entries(VAT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Примечания</label>
              <textarea style={{ ...inp, height: 60, resize: 'vertical' }} value={estimate.notes || ''}
                onChange={e => setEstimate(prev => ({ ...prev, notes: e.target.value }))}
                onBlur={e => saveHeader({ notes: e.target.value })} />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        {[['items', 'Позиции сметы'], ['discounts', 'Скидки и итоги'], ['files', 'Файлы'], ['comments', 'Комментарии']].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '10px 20px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            border: 'none', borderBottom: tab === k ? '3px solid var(--accent)' : '3px solid transparent',
            background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text2)',
          }}>{v}</button>
        ))}
      </div>

      {/* Items Tab */}
      {tab === 'items' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn-add" onClick={() => setShowCatalog(true)}>+ Из справочника</button>
            <button className="btn-sm" onClick={saveItems} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить позиции'}</button>
          </div>

          {SECTIONS.map(sec => {
            const secItems = items.map((it, idx) => ({ ...it, _idx: idx })).filter(it => it.section === sec.key);
            const isCollapsed = collapsed[sec.key];
            return (
              <div key={sec.key} style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <div onClick={() => setCollapsed(prev => ({ ...prev, [sec.key]: !prev[sec.key] }))}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 16px', background: 'var(--bg3)', cursor: 'pointer', userSelect: 'none',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{sec.key}</span>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{sec.label}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>({secItems.length})</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{formatMoney(sectionTotals[sec.key] || 0)} ₽</span>
                    <span style={{ fontSize: '0.8rem' }}>{isCollapsed ? '▸' : '▾'}</span>
                  </div>
                </div>

                {!isCollapsed && (
                  <div style={{ padding: 12 }}>
                    {secItems.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: 'var(--text2)', fontSize: '0.85rem' }}>Нет позиций</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <th style={th}>№</th>
                              <th style={{ ...th, textAlign: 'left', minWidth: 200 }}>Наименование</th>
                              <th style={th}>Кол-во</th>
                              <th style={th}>Ед.</th>
                              <th style={th}>Цена, ₽</th>
                              <th style={th}>Сумма, ₽</th>
                              <th style={{ ...th, minWidth: 120 }}>Примечание</th>
                              <th style={th}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {secItems.map((it, i) => (
                              <tr key={it._idx} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={td}>{i + 1}</td>
                                <td style={td}>
                                  <input style={{ ...inpSm, width: '100%' }} value={it.name} onChange={e => updateItem(it._idx, 'name', e.target.value)} />
                                </td>
                                <td style={td}>
                                  <input style={{ ...inpSm, width: 60, textAlign: 'right' }} type="number" step="0.1" value={it.qty} onChange={e => updateItem(it._idx, 'qty', parseFloat(e.target.value) || 0)} />
                                </td>
                                <td style={td}>
                                  <select style={{ ...inpSm, width: 70 }} value={it.unit} onChange={e => updateItem(it._idx, 'unit', e.target.value)}>
                                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </td>
                                <td style={td}>
                                  <input style={{ ...inpSm, width: 90, textAlign: 'right' }} type="number" value={it.price} onChange={e => updateItem(it._idx, 'price', parseFloat(e.target.value) || 0)} />
                                </td>
                                <td style={{ ...td, fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  {formatMoney((it.qty || 0) * (it.price || 0))}
                                </td>
                                <td style={td}>
                                  <input style={{ ...inpSm, width: '100%' }} value={it.note || ''} onChange={e => updateItem(it._idx, 'note', e.target.value)} placeholder="—" />
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                  <button onClick={() => moveItem(it._idx, -1)} style={btnMini} title="Вверх">↑</button>
                                  <button onClick={() => moveItem(it._idx, 1)} style={btnMini} title="Вниз">↓</button>
                                  <button onClick={() => removeItem(it._idx)} style={{ ...btnMini, color: 'var(--danger)' }} title="Удалить">×</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button className="btn-sm" onClick={() => addItem(sec.key)} style={{ marginTop: 8, fontSize: '0.8rem' }}>+ Добавить позицию</button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary bar */}
          <div style={{
            position: 'sticky', bottom: 0, background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: '12px 20px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginTop: 16, zIndex: 10,
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text2)' }}>Позиций: {items.length}</div>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>Без скидки</div>
                <div style={{ fontWeight: 700 }}>{formatMoney(grandTotal)} ₽</div>
              </div>
              {totalDiscount > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>Скидка {totalDiscount}%</div>
                  <div style={{ fontWeight: 700, color: 'var(--success)' }}>−{formatMoney(grandTotal - afterDiscount)} ₽</div>
                </div>
              )}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>Итого с НДС ({VAT_TYPES[estimate.vat_type]})</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--accent)' }}>{formatMoney(withVAT)} ₽</div>
              </div>
              <button className="btn-add" onClick={saveItems} disabled={saving}>{saving ? '...' : 'Сохранить'}</button>
              {uiShow('ui_estimate_download_kp') && <a href={api.exportEstimatePDF(estimateId)} target="_blank" rel="noopener noreferrer" className="btn-sm"
                style={{ background: 'var(--danger)', color: '#fff', border: 'none', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 14px', fontWeight: 700 }}>СКАЧАТЬ КП (PDF)</a>}
              {uiShow('ui_estimate_download_excel') && <a href={api.exportEstimateExcel(estimateId)} target="_blank" rel="noopener noreferrer" className="btn-sm"
                style={{ background: 'var(--success)', color: '#fff', border: 'none', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 14px', fontWeight: 700 }}>СКАЧАТЬ СМЕТУ (Excel)</a>}
            </div>
          </div>
        </div>
      )}

      {/* Discounts Tab */}
      {tab === 'discounts' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 16px' }}>Скидки</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '0.9rem' }}>Стандартная скидка (раннее подписание)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input style={{ ...inpSm, width: 60, textAlign: 'right' }} type="number" step="1" min="0" max="50"
                    value={estimate.discount_standard} onChange={e => { const v = parseFloat(e.target.value) || 0; setEstimate(prev => ({ ...prev, discount_standard: v })); saveHeader({ discount_standard: v }); }} />
                  <span style={{ fontSize: '0.9rem' }}>%</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '0.9rem' }}>Скидка постоянного клиента</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input style={{ ...inpSm, width: 60, textAlign: 'right' }} type="number" step="1" min="0" max="50"
                    value={estimate.discount_client} onChange={e => { const v = parseFloat(e.target.value) || 0; setEstimate(prev => ({ ...prev, discount_client: v })); saveHeader({ discount_client: v }); }} />
                  <span style={{ fontSize: '0.9rem' }}>%</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '0.9rem' }}>Дополнительная скидка</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input style={{ ...inpSm, width: 60, textAlign: 'right' }} type="number" step="1" min="0" max="50"
                    value={estimate.discount_custom} onChange={e => { const v = parseFloat(e.target.value) || 0; setEstimate(prev => ({ ...prev, discount_custom: v })); saveHeader({ discount_custom: v }); }} />
                  <span style={{ fontSize: '0.9rem' }}>%</span>
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700 }}>Суммарная скидка</span>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>{totalDiscount}%</span>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 }}>
            <h3 style={{ margin: '0 0 16px' }}>Итоговая таблица</h3>
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                {SECTIONS.map(sec => {
                  const t = sectionTotals[sec.key];
                  if (!t) return null;
                  return (
                    <tr key={sec.key} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 0', color: 'var(--text2)' }}>{sec.key}. {sec.label}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 500 }}>{formatMoney(t)} ₽</td>
                    </tr>
                  );
                })}
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <td style={{ padding: '8px 0', fontWeight: 700 }}>Итого без скидки</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700 }}>{formatMoney(grandTotal)} ₽</td>
                </tr>
                {totalDiscount > 0 && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 0', color: 'var(--success)' }}>Скидка {totalDiscount}%</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', color: 'var(--success)', fontWeight: 500 }}>−{formatMoney(grandTotal - afterDiscount)} ₽</td>
                  </tr>
                )}
                {totalDiscount > 0 && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 0' }}>Итого со скидкой</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>{formatMoney(afterDiscount)} ₽</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: '10px 0', fontWeight: 700, fontSize: '1rem' }}>Итого с НДС ({VAT_TYPES[estimate.vat_type]})</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 700, fontSize: '1.1rem', color: 'var(--accent)' }}>{formatMoney(withVAT)} ₽</td>
                </tr>
                <tr>
                  <td colSpan="2" style={{ padding: '0 0 4px', fontSize: '0.75rem', color: 'var(--text2)' }}>{taxFormula(estimate.vat_type, taxSettings)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Files Tab */}
      {tab === 'files' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} id="est-file-input" />
            <label htmlFor="est-file-input" className="btn-add" style={{ cursor: 'pointer', display: 'inline-block' }}>Загрузить файлы</label>
            {uiShow('ui_estimate_recognize') && <button className="btn-add" onClick={handleRecognize} disabled={recognizing || files.filter(f => /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(f.original_name || f.filename)).length === 0}
              style={{ background: 'var(--accent)', opacity: recognizing ? 0.6 : 1 }}>
              {recognizing ? 'Анализ...' : 'РАСПОЗНАТЬ'}
            </button>}
          </div>

          {/* Stand parameters */}
          {uiShow('ui_estimate_stand_params') && <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text2)' }}>ПАРАМЕТРЫ СТЕНДА</h4>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ ...lbl, marginBottom: 4 }}>Длина, м</label>
                <input style={{ ...inpSm, width: 90 }} type="number" step="0.1" min="0" placeholder="—"
                  value={standParams.length} onChange={e => setStandParams(p => ({ ...p, length: e.target.value }))} />
              </div>
              <div>
                <label style={{ ...lbl, marginBottom: 4 }}>Ширина, м</label>
                <input style={{ ...inpSm, width: 90 }} type="number" step="0.1" min="0" placeholder="—"
                  value={standParams.width} onChange={e => setStandParams(p => ({ ...p, width: e.target.value }))} />
              </div>
              <div>
                <label style={{ ...lbl, marginBottom: 4 }}>Высота стен, м</label>
                <input style={{ ...inpSm, width: 90 }} type="number" step="0.1" min="0" placeholder="2.5"
                  value={standParams.wall_height} onChange={e => setStandParams(p => ({ ...p, wall_height: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.85rem', paddingBottom: 2 }}>
                <input type="checkbox" checked={standParams.has_suspension}
                  onChange={e => setStandParams(p => ({ ...p, has_suspension: e.target.checked }))} />
                <span style={{ fontWeight: 600 }}>Наличие подвеса</span>
              </label>
            </div>
          </div>}

          {/* Model selection */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text2)' }}>ПОДКЛЮЧИТЬ МОДЕЛИ</h4>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={aiModels.openai} onChange={e => setAiModels(prev => ({ ...prev, openai: e.target.checked }))} />
                <span style={{ fontWeight: 600 }}>OpenAI (ChatGPT)</span>
                <select value={aiModels.openai_model} onChange={e => setAiModels(prev => ({ ...prev, openai_model: e.target.value }))}
                  style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.8rem' }}
                  disabled={!aiModels.openai}>
                  <option value="gpt-5.6-sol">GPT-5.6 Sol ($$$)</option>
                  <option value="gpt-5.6-terra">GPT-5.6 Terra ($$)</option>
                  <option value="gpt-5.6-luna">GPT-5.6 Luna ($)</option>
                  <option value="gpt-6-astra">GPT-6 Astra ($$$$)</option>
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={aiModels.claude} onChange={e => setAiModels(prev => ({ ...prev, claude: e.target.checked }))} />
                <span style={{ fontWeight: 600 }}>Claude (Anthropic)</span>
                <select value={aiModels.claude_model} onChange={e => setAiModels(prev => ({ ...prev, claude_model: e.target.value }))}
                  style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.8rem' }}
                  disabled={!aiModels.claude}>
                  <option value="claude-sonnet-4-20250514">Claude Sonnet 4 ($$)</option>
                  <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 ($)</option>
                </select>
              </label>
            </div>
          </div>

          {/* Recognizing progress */}
          {recognizing && (
            <div style={{ padding: 24, textAlign: 'center', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
              <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>AI анализирует изображения...</div>
              <div style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>Это может занять 30-60 секунд</div>
              <div style={{ marginTop: 12, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--accent)', borderRadius: 2, animation: 'progress 2s ease-in-out infinite', width: '40%' }} />
              </div>
              <style>{`@keyframes progress { 0% { margin-left: 0; } 50% { margin-left: 60%; } 100% { margin-left: 0; } }`}</style>
            </div>
          )}

          {/* Recognition result */}
          {recognizeResult && (
            <div style={{ background: 'var(--bg2)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, color: 'var(--accent)' }}>Результат AI-анализа</h4>
                <button className="btn-add" onClick={acceptRecognition}>Применить ({recognizeResult.items?.length || 0} позиций)</button>
              </div>
              {recognizeResult.description && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: 12, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 6 }}>
                  {recognizeResult.description}
                </div>
              )}
              {recognizeResult.applied_rules > 0 && (
                <div style={{ fontSize: '0.8rem', marginBottom: 8, color: 'var(--success)', fontWeight: 600 }}>
                  Учтено правил администратора: {recognizeResult.applied_rules}
                </div>
              )}
              {recognizeResult.area_sqm && (
                <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>Определённая площадь: <strong>{recognizeResult.area_sqm} кв.м</strong></div>
              )}
              <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                      <th style={{ ...th, textAlign: 'left' }}>Секция</th>
                      <th style={{ ...th, textAlign: 'left', minWidth: 200 }}>Наименование</th>
                      <th style={th}>Кол-во</th>
                      <th style={th}>Ед.</th>
                      <th style={th}>Цена</th>
                      <th style={th}>Сумма</th>
                      <th style={{ ...th, textAlign: 'left' }}>Прим.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recognizeResult.items || []).map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: 'var(--accent)' }}>{it.section}</td>
                        <td style={{ ...td, textAlign: 'left' }}>{it.name}</td>
                        <td style={td}>{it.qty}</td>
                        <td style={td}>{it.unit}</td>
                        <td style={td}>{formatMoney(it.price || 0)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{formatMoney((it.qty || 0) * (it.price || 0))}</td>
                        <td style={{ ...td, textAlign: 'left', fontSize: '0.75rem', color: 'var(--text2)' }}>{it.note}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)' }}>
                      <td colSpan="5" style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Итого:</td>
                      <td style={{ ...td, fontWeight: 700, color: 'var(--accent)' }}>
                        {formatMoney((recognizeResult.items || []).reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0))} ₽
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* File list */}
          {files.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Нет прикреплённых файлов. Добавьте изображения проекта из чата кнопкой "В ПРОЕКТ" или загрузите вручную.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {files.map(f => {
                const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.original_name || f.filename);
                return (
                  <div key={f.id} style={{
                    width: isImage ? 180 : '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    overflow: 'hidden',
                  }}>
                    {isImage && (
                      <img src={`/api/estimates/file/${f.filename}?token=${localStorage.getItem('token')}`}
                        alt="" style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                    )}
                    <div style={{ padding: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text2)' }}>{(f.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => deleteFile(f.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.8rem' }}>×</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {tab === 'comments' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input style={{ ...inp, flex: 1 }} value={commentText} onChange={e => setCommentText(e.target.value)}
              placeholder="Написать комментарий..." onKeyDown={e => e.key === 'Enter' && addComment()} />
            <button className="btn-add" onClick={addComment}>Отправить</button>
          </div>
          {comments.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Нет комментариев</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {comments.map(c => (
                <div key={c.id} style={{ padding: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.user_name}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>{formatDate(c.created_at)}</span>
                      <button onClick={() => deleteComment(c.id)} style={{ ...btnMini, color: 'var(--danger)' }}>×</button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>{c.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Catalog Modal */}
      {showCatalog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowCatalog(false)}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            width: '90%', maxWidth: 800, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Справочник позиций</h3>
              <button onClick={() => setShowCatalog(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text2)' }}>×</button>
            </div>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8 }}>
              <input style={{ ...inp, flex: 1 }} placeholder="Поиск..." value={catalogFilter} onChange={e => setCatalogFilter(e.target.value)} />
              <select style={inp} value={catalogSection} onChange={e => setCatalogSection(e.target.value)}>
                <option value="all">Все секции</option>
                {SECTIONS.map(s => <option key={s.key} value={s.key}>{s.key}. {s.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {filteredCatalog.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text2)', padding: 20 }}>Ничего не найдено</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg)' }}>
                      <th style={{ ...th, textAlign: 'left' }}>Наименование</th>
                      <th style={th}>Ед.</th>
                      <th style={th}>Эконом</th>
                      <th style={th}>Стандарт</th>
                      <th style={th}>Премиум</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCatalog.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--accent)', marginRight: 6, fontWeight: 700 }}>{c.section}</span>
                          {c.name}
                          {c.note && <span style={{ color: 'var(--text2)', fontSize: '0.75rem', marginLeft: 4 }}>({c.note})</span>}
                        </td>
                        <td style={td}>{c.unit}</td>
                        <td style={td}>
                          <button onClick={() => addFromCatalog(c, 'economy')} style={btnPrice}>{formatMoney(c.price_economy)}</button>
                        </td>
                        <td style={td}>
                          <button onClick={() => addFromCatalog(c, 'standard')} style={{ ...btnPrice, background: 'var(--accent)', color: '#fff' }}>{formatMoney(c.price_standard)}</button>
                        </td>
                        <td style={td}>
                          <button onClick={() => addFromCatalog(c, 'premium')} style={{ ...btnPrice, background: 'var(--warning)', color: '#000' }}>{formatMoney(c.price_premium)}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const lbl = { display: 'block', fontSize: '0.8rem', color: 'var(--text2)', marginBottom: 4, fontWeight: 600 };
const inp = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.9rem', boxSizing: 'border-box' };
const inpSm = { padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.83rem', boxSizing: 'border-box' };
const th = { padding: '6px 8px', textAlign: 'right', fontSize: '0.8rem', color: 'var(--text2)', fontWeight: 600 };
const td = { padding: '4px 8px', textAlign: 'right' };
const btnMini = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 4px', color: 'var(--text2)' };
const btnPrice = { padding: '3px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 };

// ====== Training Modal ======
function TrainingModal({ onClose }) {
  const [projects, setProjects] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [training, setTraining] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [trainResult, setTrainResult] = useState(null);
  const [selectedForTraining, setSelectedForTraining] = useState(new Set());
  const [msg, setMsg] = useState('');
  const [knowledgeInfo, setKnowledgeInfo] = useState(null);

  useEffect(() => {
    loadProjects();
    api.getKnowledgeBase().then(setKnowledgeInfo).catch(() => {});
  }, []);

  async function loadProjects() {
    try {
      const data = await api.getTrainingProjects();
      setProjects(data);
    } catch {}
  }

  async function loadFiles(projectId) {
    try {
      const files = await api.getTrainingFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: files }));
    } catch {}
  }

  async function addProject() {
    if (!newName.trim()) return;
    try {
      await api.createTrainingProject(newName.trim(), newDesc.trim());
      setNewName('');
      setNewDesc('');
      loadProjects();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  async function deleteProject(id) {
    if (!window.confirm('Удалить проект обучения и все его файлы?')) return;
    try {
      await api.deleteTrainingProject(id);
      setSelectedProject(null);
      loadProjects();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
  }

  async function handleUpload(projectId, e) {
    const fileList = e.target.files;
    if (!fileList.length) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      const fileNames = [];
      for (const f of fileList) {
        formData.append('files', f);
        fileNames.push(f.name);
      }
      formData.append('original_names', JSON.stringify(fileNames));
      const token = localStorage.getItem('token');
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/training/projects/${projectId}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          try { const d = JSON.parse(xhr.responseText); xhr.status >= 400 ? reject(new Error(d.error || 'Upload failed')) : resolve(d); }
          catch { reject(new Error('Upload failed')); }
        };
        xhr.onerror = () => reject(new Error('Ошибка сети'));
        xhr.send(formData);
      });
      loadFiles(projectId);
      loadProjects();
      if (result.hasDuplicates) {
        const dupNames = result.duplicates.slice(0, 5).join(', ');
        const extra = result.duplicates.length > 5 ? ` и ещё ${result.duplicates.length - 5}` : '';
        setMsg(`Загружено ${result.files.length} новых файлов. ${result.duplicates.length} файлов уже есть в базе знаний (${dupNames}${extra}) — повторная загрузка не требуется.`);
      } else {
        setMsg(`Загружено ${result.files?.length || fileList.length} файлов`);
      }
    } catch (err) { setMsg('Ошибка загрузки: ' + err.message); }
    setUploading(false);
    setUploadProgress(0);
    e.target.value = '';
  }

  async function deleteFile(fileId, projectId) {
    try {
      await api.deleteTrainingFile(fileId);
      loadFiles(projectId);
    } catch {}
  }

  function toggleSelect(id) {
    setSelectedForTraining(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function startTraining() {
    const ids = Array.from(selectedForTraining);
    if (ids.length === 0) { setMsg('Выберите проекты для обучения'); return; }
    setTraining(true);
    setProgress(0);
    setTrainResult(null);
    setProgressMsg('Подготовка данных...');

    const steps = ['Подготовка данных...', 'Загрузка изображений...', 'AI анализирует проекты...', 'Извлечение паттернов...', 'Формирование базы знаний...', 'Сохранение результатов...'];
    let step = 0;
    const interval = setInterval(() => {
      step++;
      if (step < steps.length) {
        setProgressMsg(steps[step]);
        setProgress(Math.min(15 + step * 14, 85));
      }
    }, 4000);

    const progInterval = setInterval(() => {
      setProgress(prev => prev < 85 ? prev + 1 : prev);
    }, 800);

    try {
      const result = await api.startTraining(ids);
      clearInterval(interval);
      clearInterval(progInterval);
      setProgress(100);
      setProgressMsg('Обучение завершено!');
      setTrainResult(result);
      loadProjects();
      api.getKnowledgeBase().then(setKnowledgeInfo).catch(() => {});
    } catch (err) {
      clearInterval(interval);
      clearInterval(progInterval);
      setProgress(0);
      setProgressMsg('');
      setMsg('Ошибка обучения: ' + err.message);
    }
    setTraining(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        width: '95%', maxWidth: 1000, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>ОБУЧЕНИЕ ПО ПРОЕКТАМ</h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginTop: 4 }}>
              Загрузите фото реальных проектов — система изучит их и будет точнее считать сметы
              {knowledgeInfo?.has_knowledge && (
                <span style={{ marginLeft: 8, color: 'var(--success)', fontWeight: 600 }}>
                  База знаний: {knowledgeInfo.projects_count} проект(ов)
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text2)' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {msg && <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, fontSize: '0.85rem', background: msg.startsWith('Ош') ? 'rgba(231,76,60,0.1)' : 'rgba(46,204,113,0.1)', color: msg.startsWith('Ош') ? 'var(--danger)' : 'var(--success)' }}>{msg}</div>}

          {/* Add project */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>Добавить проект</h4>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input style={{ ...inp, flex: 2, minWidth: 200 }} placeholder="Название проекта (например: Стенд Газпром 2025)" value={newName} onChange={e => setNewName(e.target.value)} />
              <input style={{ ...inp, flex: 1, minWidth: 150 }} placeholder="Описание (необязательно)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
              <button className="btn-add" onClick={addProject} disabled={!newName.trim()}>Добавить</button>
            </div>
          </div>

          {/* Projects list */}
          {projects.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text2)' }}>
              Нет проектов для обучения. Добавьте первый проект и загрузите в него фотографии.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {projects.map(p => {
                const isOpen = selectedProject === p.id;
                const files = projectFiles[p.id] || [];
                const imageCount = files.filter(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.original_name || f.filename)).length;
                const docCount = files.filter(f => /\.(pdf|xlsx?|docx?)$/i.test(f.original_name || f.filename)).length;
                const isSelected = selectedForTraining.has(p.id);

                return (
                  <div key={p.id} style={{ background: 'var(--bg2)', border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--radius)', overflow: 'hidden', transition: 'border-color 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(p.id)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }} />
                      <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => { if (!isOpen) { setSelectedProject(p.id); loadFiles(p.id); } else setSelectedProject(null); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name}</span>
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 10,
                            background: p.status === 'trained' ? 'rgba(46,204,113,0.15)' : 'rgba(136,136,136,0.15)',
                            color: p.status === 'trained' ? 'var(--success)' : 'var(--text2)', fontWeight: 600,
                          }}>{p.status === 'trained' ? 'Обучено' : 'Ожидание'}</span>
                        </div>
                        {p.description && <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginTop: 2 }}>{p.description}</div>}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text2)', marginTop: 2 }}>
                          Файлов: {p.file_count || 0} · {formatDate(p.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-sm" onClick={() => { if (!isOpen) { setSelectedProject(p.id); loadFiles(p.id); } else setSelectedProject(null); }}>
                          {isOpen ? 'Свернуть' : 'Файлы'}
                        </button>
                        <button onClick={() => deleteProject(p.id)} style={{ ...btnMini, color: 'var(--danger)', fontSize: '1.1rem' }}>×</button>
                      </div>
                    </div>

                    {isOpen && (
                      <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 12, alignItems: 'center' }}>
                          <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" id={`train-upload-${p.id}`}
                            onChange={e => handleUpload(p.id, e)} style={{ display: 'none' }} />
                          <label htmlFor={`train-upload-${p.id}`} className="btn-add" style={{ cursor: 'pointer', display: 'inline-block', opacity: uploading ? 0.6 : 1 }}>
                            {uploading ? `Загрузка ${uploadProgress}%` : 'Загрузить файлы'}
                          </label>
                          {uploading && (
                            <div style={{ flex: 1, minWidth: 100, maxWidth: 200, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', background: uploadProgress === 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 4, transition: 'width 0.3s', width: `${uploadProgress}%` }} />
                            </div>
                          )}
                          <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>
                            {imageCount > 0 && `${imageCount} фото`}{imageCount > 0 && docCount > 0 && ' · '}{docCount > 0 && `${docCount} док.`}{files.length > 0 && ` · ${files.length} всего`}
                          </span>
                        </div>

                        {files.length === 0 ? (
                          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)', fontSize: '0.85rem' }}>
                            Загрузите фотографии готового стенда (общий вид, детали, элементы)
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {files.map(f => {
                              const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.original_name || f.filename);
                              return (
                                <div key={f.id} style={{ width: isImage ? 120 : '100%', background: 'var(--bg3)', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                                  {isImage && (
                                    <img src={`/api/training/file/${f.filename}?token=${localStorage.getItem('token')}`}
                                      alt="" style={{ width: '100%', height: 90, objectFit: 'cover' }} />
                                  )}
                                  <div style={{ padding: '4px 6px' }}>
                                    <div style={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.65rem', color: 'var(--text2)' }}>{(f.size / 1024).toFixed(0)} KB</span>
                                      <button onClick={() => deleteFile(f.id, p.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.75rem' }}>×</button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Training progress */}
          {training && (
            <div style={{ background: 'var(--bg2)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 24, marginTop: 16, textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>{progressMsg}</div>
              <div style={{ background: 'var(--border)', borderRadius: 8, height: 20, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{
                  height: '100%', background: progress === 100 ? 'var(--success)' : 'var(--accent)',
                  borderRadius: 8, transition: 'width 0.5s ease', width: `${progress}%`,
                }} />
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text2)' }}>{progress}%</div>
            </div>
          )}

          {/* Training result */}
          {trainResult && (
            <div style={{ background: 'var(--bg2)', border: '2px solid var(--success)', borderRadius: 'var(--radius)', padding: 20, marginTop: 16 }}>
              <h4 style={{ margin: '0 0 12px', color: 'var(--success)' }}>
                {trainResult.summary?.projects_failed > 0 ? 'Обучение завершено (с ошибками)' : 'Система обучена!'}
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
                {[
                  ['Проектов обучено', trainResult.summary?.projects_trained || 0],
                  ['Файлов изучено', trainResult.summary?.total_files_analyzed || 0],
                  ['Данных обработано', (trainResult.summary?.total_data_size_mb || '0') + ' МБ'],
                  ['Позиций изучено', trainResult.summary?.total_items_learned || 0],
                  ['Паттернов найдено', trainResult.summary?.total_patterns_found || 0],
                ].map(([label, val]) => (
                  <div key={label} style={{ background: 'var(--bg3)', borderRadius: 6, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--accent)' }}>{val}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>{label}</div>
                  </div>
                ))}
              </div>

              {(trainResult.results || []).map((r, idx) => (
                <div key={r.project_id || idx} style={{ background: r.error ? 'rgba(231,76,60,0.08)' : 'var(--bg3)', border: `1px solid ${r.error ? 'var(--danger)' : 'var(--border)'}`, borderRadius: 8, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: '1.1rem' }}>{r.error ? '⚠️' : '✅'}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Проект «{r.name}»</span>
                    {r.error && <span style={{ fontSize: '0.8rem', color: 'var(--danger)', marginLeft: 'auto' }}>Ошибка</span>}
                  </div>

                  {r.error ? (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text2)', padding: '8px 12px', background: 'rgba(231,76,60,0.05)', borderRadius: 4 }}>
                      {r.error}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text2)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
                        {r.project_type && <div>Тип: <strong>{r.project_type}</strong></div>}
                        {r.estimated_area_sqm && <div>Площадь: <strong>{r.estimated_area_sqm} кв.м</strong></div>}
                        {r.quality_level && <div>Уровень: <strong>{r.quality_level}</strong></div>}
                        {r.style && <div>Стиль: <strong>{r.style}</strong></div>}
                      </div>
                      {r.materials?.length > 0 && (
                        <div style={{ fontSize: '0.8rem', marginTop: 6 }}>
                          <span style={{ color: 'var(--text2)' }}>Материалы:</span> {r.materials.join(', ')}
                        </div>
                      )}
                      {r.key_features?.length > 0 && (
                        <div style={{ fontSize: '0.8rem', marginTop: 4 }}>
                          <span style={{ color: 'var(--text2)' }}>Особенности:</span> {r.key_features.join(', ')}
                        </div>
                      )}
                      {r.patterns?.length > 0 && (
                        <div style={{ fontSize: '0.8rem', marginTop: 6 }}>
                          <div style={{ color: 'var(--accent)', marginBottom: 4, fontWeight: 600 }}>Паттерны и закономерности:</div>
                          {r.patterns.map((p, i) => <div key={i} style={{ paddingLeft: 8, marginBottom: 2 }}>• {p}</div>)}
                        </div>
                      )}
                      {r.lessons && (
                        <div style={{ fontSize: '0.8rem', marginTop: 8, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 4, borderLeft: '3px solid var(--accent)' }}>
                          <div style={{ color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>Выводы:</div>
                          {r.lessons}
                        </div>
                      )}
                      {r.items?.length > 0 && (
                        <div style={{ fontSize: '0.8rem', marginTop: 8, color: 'var(--text2)' }}>
                          Изучено позиций: <strong>{r.items.length}</strong>, итого по проекту: <strong>{formatMoney(r.total_estimate || 0)} ₽</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with training button */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--text2)' }}>
            Выбрано для обучения: <strong>{selectedForTraining.size}</strong> проект(ов)
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-sm" onClick={onClose}>Закрыть</button>
            <button className="btn-add" onClick={startTraining}
              disabled={training || selectedForTraining.size === 0}
              style={{ background: training ? 'var(--text2)' : 'var(--accent)', opacity: selectedForTraining.size === 0 ? 0.5 : 1, padding: '8px 24px', fontSize: '0.9rem', fontWeight: 700 }}>
              {training ? 'Обучение...' : 'НАЧАТЬ ОБУЧЕНИЕ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Terminal Modal (только админ) ======
// Диалог с ассистентом: админ пишет корректировки, ассистент превращает их в правила,
// которые учитываются при каждом распознавании смет. Всё хранится на сервере.
function TerminalModal({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [rules, setRules] = useState([]);
  const [tax, setTax] = useState(DEFAULT_TAX);
  const [taxDraft, setTaxDraft] = useState(DEFAULT_TAX);
  const [maxRules, setMaxRules] = useState(100);
  const [input, setInput] = useState('');
  const [model, setModel] = useState('gpt-5.6-terra');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [manualRule, setManualRule] = useState('');
  const [editRule, setEditRule] = useState(null); // { id, text }
  const [showOff, setShowOff] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.terminalState().then(s => {
      setMessages(s.messages || []);
      setRules(s.rules || []);
      setTax(s.tax || DEFAULT_TAX);
      setTaxDraft(s.tax || DEFAULT_TAX);
      setMaxRules(s.max_rules || 100);
    }).catch(e => setErr('Не удалось загрузить терминал: ' + e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setErr('');
    try {
      const r = await api.terminalSend(text, model);
      setMessages(prev => [...prev, ...r.messages]);
      setRules(r.rules);
      setInput('');
    } catch (e) { setErr(e.message); }
    setSending(false);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  async function toggleRule(rule) {
    try {
      const r = await api.terminalUpdateRule(rule.id, { active: !rule.active });
      setRules(r.rules);
    } catch (e) { setErr(e.message); }
  }

  async function saveRuleEdit() {
    if (!editRule || !editRule.text.trim()) return;
    try {
      const r = await api.terminalUpdateRule(editRule.id, { text: editRule.text });
      setRules(r.rules);
      setEditRule(null);
    } catch (e) { setErr(e.message); }
  }

  async function addManualRule() {
    if (!manualRule.trim()) return;
    try {
      const r = await api.terminalAddRule(manualRule);
      setRules(r.rules);
      setManualRule('');
      if (r.duplicate) setNote('Такое правило уже есть');
    } catch (e) { setErr(e.message); }
  }

  async function saveTax() {
    try {
      const r = await api.terminalSaveSettings(taxDraft);
      setTax(r.tax);
      setTaxDraft(r.tax);
      setNote('Налоговые настройки сохранены — применяются сразу ко всем сметам и выгрузкам');
    } catch (e) { setErr(e.message); }
  }

  const activeRules = rules.filter(r => r.active);
  const offRules = rules.filter(r => !r.active);
  const taxDirty = taxDraft.prices_include_vat !== tax.prices_include_vat || Number(taxDraft.prices_vat_rate) !== Number(tax.prices_vat_rate);
  const panel = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 12 };

  function ruleCard(r) {
    const editing = editRule && editRule.id === r.id;
    return (
      <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', marginBottom: 6, background: 'var(--bg3)', opacity: r.active ? 1 : 0.55 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--accent)' }}>№{r.id}{r.source === 'manual' ? ' · вручную' : ''}</span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!editing && <button onClick={() => setEditRule({ id: r.id, text: r.text })} style={{ ...btnMini, fontSize: '0.75rem' }}>ред.</button>}
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!r.active} onChange={() => toggleRule(r)} /> {r.active ? 'активно' : 'выкл.'}
            </label>
          </span>
        </div>
        {editing ? (
          <div>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={editRule.text} onChange={e => setEditRule({ ...editRule, text: e.target.value })} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button className="btn-sm" onClick={saveRuleEdit}>Сохранить</button>
              <button className="btn-sm" onClick={() => setEditRule(null)}>Отмена</button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.text}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', width: '96%', maxWidth: 1200, height: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: 'monospace' }}>&gt;_ ТЕРМИНАЛ СМЕТНОЙ СИСТЕМЫ</h3>
            <div style={{ fontSize: '0.78rem', color: 'var(--text2)', marginTop: 4 }}>
              Пишите корректировки обычным текстом — ассистент оформит их в правила, и они будут учитываться во всех новых расчётах смет
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select value={model} onChange={e => setModel(e.target.value)} disabled={sending}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: '0.8rem' }}>
              {TERMINAL_MODELS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--text2)' }}>×</button>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', gap: 16, padding: 16, minHeight: 0, flexWrap: 'wrap', overflowY: 'auto' }}>
          {/* Chat column */}
          <div style={{ flex: '2 1 420px', display: 'flex', flexDirection: 'column', minHeight: 360, minWidth: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 4px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>Загрузка...</div>}
              {!loading && messages.length === 0 && (
                <div style={{ color: 'var(--text2)', fontSize: '0.88rem', lineHeight: 1.6, padding: 12, border: '1px dashed var(--border)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Как это работает</div>
                  Напишите, что система считает не так, — например:
                  <ul style={{ margin: '6px 0 6px 18px', padding: 0 }}>
                    <li>«Стены считай по периметру × высоту, без вычета проёмов»</li>
                    <li>«Монтаж всегда отдельной позицией, 15% от стоимости конструкций»</li>
                    <li>«Ковролин в расчёт не включай, если клиент не просил»</li>
                  </ul>
                  Ассистент запишет правило и покажет его справа. Дальше нажмите «РАСПОЗНАТЬ» в смете — правило уже будет учтено.
                  Можно и просто спросить: «какие правила сейчас действуют?».
                </div>
              )}
              {messages.map(m => (
                <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                  <div style={{
                    padding: '8px 12px', borderRadius: 10, fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    background: m.role === 'user' ? 'var(--accent)' : 'var(--bg2)', color: m.role === 'user' ? '#fff' : 'var(--text)',
                    border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                  }}>{m.content}</div>
                  {(m.actions || []).map((a, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', marginTop: 4, padding: '4px 8px', borderRadius: 6, lineHeight: 1.4,
                      background: a.type === 'add' ? 'rgba(0,184,148,0.12)' : a.type === 'off' ? 'var(--bg3)' : 'rgba(231,76,60,0.12)',
                      color: a.type === 'add' ? 'var(--success)' : a.type === 'off' ? 'var(--text2)' : 'var(--danger)' }}>
                      {a.type === 'add' && <>✓ {a.duplicate ? `Такое правило уже было (№${a.id})` : `Записано правило №${a.id}`}: {a.text}</>}
                      {a.type === 'off' && <>Отключено правило №{a.id}: {a.text}</>}
                      {a.type === 'error' && <>Не записано: {a.text}</>}
                    </div>
                  ))}
                </div>
              ))}
              {sending && <div style={{ alignSelf: 'flex-start', color: 'var(--text2)', fontSize: '0.85rem', fontFamily: 'monospace' }}>ассистент думает…</div>}
              <div ref={endRef} />
            </div>

            {err && <div style={{ color: 'var(--danger)', fontSize: '0.82rem', margin: '4px 0', whiteSpace: 'pre-wrap' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 6 }}>
              <textarea style={{ ...inp, minHeight: 56, maxHeight: 160, resize: 'vertical', flex: 1 }} value={input} disabled={sending}
                placeholder="Корректировка или вопрос… (Enter — отправить, Shift+Enter — новая строка)"
                onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} />
              <button className="btn-add" onClick={send} disabled={sending || !input.trim()} style={{ height: 56, opacity: sending || !input.trim() ? 0.6 : 1 }}>
                {sending ? '...' : 'Отправить'}
              </button>
            </div>
          </div>

          {/* Side panel: tax + rules */}
          <div style={{ flex: '1 1 300px', minWidth: 0, overflowY: 'auto' }}>
            <div style={panel}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>Налоги в итоге сметы</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={taxDraft.prices_include_vat} onChange={e => setTaxDraft(d => ({ ...d, prices_include_vat: e.target.checked }))} />
                Цены в смете уже включают НДС
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', marginBottom: 8, opacity: taxDraft.prices_include_vat ? 1 : 0.5 }}>
                НДС в ценах:
                <input style={{ ...inpSm, width: 60, textAlign: 'right' }} type="number" min="0" max="50" step="1" disabled={!taxDraft.prices_include_vat}
                  value={taxDraft.prices_vat_rate} onChange={e => setTaxDraft(d => ({ ...d, prices_vat_rate: e.target.value }))} /> %
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8 }}>
                {Object.keys(VAT_TYPES).map(k => (
                  <div key={k}>{VAT_TYPES[k]}: {taxFormula(k, { prices_include_vat: taxDraft.prices_include_vat, prices_vat_rate: Number(taxDraft.prices_vat_rate) || 0 }).replace(/^Цены[^:]*: /, '')}</div>
                ))}
              </div>
              <button className="btn-sm" onClick={saveTax} disabled={!taxDirty} style={{ opacity: taxDirty ? 1 : 0.5 }}>Сохранить</button>
              {note && <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 6 }}>{note}</div>}
            </div>

            <div style={panel}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>
                Действующие правила ({activeRules.length}/{maxRules})
              </div>
              {activeRules.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>Пока нет правил. Напишите первую корректировку в чате слева.</div>}
              {activeRules.map(ruleCard)}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input style={{ ...inpSm, flex: 1, minWidth: 0 }} value={manualRule} placeholder="Добавить правило вручную…"
                  onChange={e => setManualRule(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addManualRule(); }} />
                <button className="btn-sm" onClick={addManualRule} disabled={!manualRule.trim()}>+</button>
              </div>
              {offRules.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button className="btn-sm" onClick={() => setShowOff(v => !v)}>{showOff ? 'Скрыть' : 'Показать'} отключённые ({offRules.length})</button>
                  {showOff && <div style={{ marginTop: 8 }}>{offRules.map(ruleCard)}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Main Component ======
export default function EstimatesPage({ uiVis = {}, isAdmin = false }) {
  const [view, setView] = useState('list');
  const [editId, setEditId] = useState(null);
  const [showTraining, setShowTraining] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const uiShow = (id) => uiVis[id] !== false;

  async function handleNew() {
    try {
      const result = await api.createEstimate({ client_name: '', project_title: '' });
      setEditId(result.id);
      setView('edit');
    } catch {}
  }

  if (view === 'edit' && editId) {
    return (
      <div className="admin-page">
        <EstimateEditor estimateId={editId} onBack={() => { setView('list'); setEditId(null); }} isNew={!editId} uiShow={uiShow} />
        {showTraining && <TrainingModal onClose={() => setShowTraining(false)} />}
      </div>
    );
  }

  return (
    <div className="admin-page">
      <EstimatesList onOpen={(id) => { setEditId(id); setView('edit'); }} onNew={handleNew}
        onTraining={() => setShowTraining(true)} onTerminal={isAdmin ? () => setShowTerminal(true) : null} uiShow={uiShow} />
      {showTraining && <TrainingModal onClose={() => setShowTraining(false)} />}
      {isAdmin && showTerminal && <TerminalModal onClose={() => setShowTerminal(false)} />}
    </div>
  );
}
